import type { CSSProperties } from "react";

import type { FooterNavLink } from "@ui/eva-components/Footer/types/FooterNavLink.type";
import { useTranslations } from "@ui/providers/useTranslations";

import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import styles from "../FooterNav/FooterNav.module.scss";

/**
 * Maps URL patterns to their corresponding tracking events.
 *
 * This approach checks page URL and test it with provided pattern.
 * This solution has some limitations and for that reason,
 * it was crucial to add gtmOverride property in CMS.
 */
const TRACKING_URL_PATTERNS = [
  { pattern: /store-locator/, event: TrackingEvents.TrackingStoreLocatorClick, name: "store_locator_click" },
  { pattern: /\/contact(-us)?($|\/|\?)/, event: TrackingEvents.TrackingContactUsClick, name: "contact_click" },
] as const;

export const FOOTER_GTM_OVERRIDE = TRACKING_URL_PATTERNS.map((item) => item.name);
export const WITHDRAWAL_MODAL_TRIGGER_URL = "/open-withdrawal-modal";

export type FooterGtmOverride = (typeof FOOTER_GTM_OVERRIDE)[number];

const dispatchLinkTrackingEvent = (url: FooterNavLink["url"], gtmOverride: FooterNavLink["gtmOverride"]) => {
  if (!url) return;
  TRACKING_URL_PATTERNS.forEach(({ pattern, event, name }: (typeof TRACKING_URL_PATTERNS)[number]) => {
    if (name === gtmOverride || pattern.test(url)) dispatchTrackingEvent(event);
  });
};

export type FooterNavLinksProps = {
  links?: FooterNavLink[];
  className?: string;
  itemClassname?: string;
  enableCookieSettings?: boolean;
  listStyle?: CSSProperties;
  cookieSettingsAriaLabel?: string;
  openAssetsInNewTab?: boolean;
  withdrawalModalTriggerUrl?: string;
  onOpenWithdrawalModal?: () => void;
};

export const FooterNavLinks = ({
  links = [],
  className = "",
  itemClassname = "",
  enableCookieSettings = false,
  listStyle,
  cookieSettingsAriaLabel,
  openAssetsInNewTab,
  withdrawalModalTriggerUrl = WITHDRAWAL_MODAL_TRIGGER_URL,
  onOpenWithdrawalModal,
}: FooterNavLinksProps) => {
  const { __ } = useTranslations();

  const handleLinkClick = (url: FooterNavLink["url"], gtmOverride: FooterNavLink["gtmOverride"]) => {
    dispatchLinkTrackingEvent(url, gtmOverride);
  };

  return (
    <ul className={className} style={listStyle} data-testid="footerNavLinks">
      {enableCookieSettings && (
        <li className={itemClassname}>
          <button
            className="ot-sdk-show-settings"
            id="ot-sdk-btn"
            aria-label={cookieSettingsAriaLabel ?? __("footer.cookie_settings")}
          />
        </li>
      )}
      {links.map(({ url, title, gtmOverride }, index) => {
        const isWithdrawalTrigger = url === withdrawalModalTriggerUrl && Boolean(onOpenWithdrawalModal);

        return (
          <li className={itemClassname} key={`${url}-${title}-${index}`}>
            {isWithdrawalTrigger ? (
              <button
                type="button"
                className={styles.link}
                onClick={() => {
                  handleLinkClick(url, gtmOverride);
                  onOpenWithdrawalModal?.();
                }}
              >
                {title}
              </button>
            ) : (
              <LinkBehaviour
                className={styles.link}
                href={url}
                {...(openAssetsInNewTab && url?.includes("assets") && { target: "_blank", rel: "noopener noreferrer" })}
                onClick={() => handleLinkClick(url, gtmOverride)}
              >
                {title}
              </LinkBehaviour>
            )}
          </li>
        );
      })}
    </ul>
  );
};
